import { PUBLIC_PATH } from '@app/shared/constants/paths.constant';
import { i18n } from '@i18n/translations';
import { Button, Card, CardContent } from '@neogeniux/uikit';
import { Home, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface AccessDeniedScreenProps {
  title?: string;
  description?: string;
  /**
   * Optional override for the home destination. Defaults to the
   * login page — the login flow already resolves the user's first
   * accessible menu entry and routes there. We can't redirect to a
   * fixed page like /dashboard because the operator may not have
   * permission to it either, which would just bounce them back to
   * the same access-denied screen.
   */
  homePath?: string;
}

/**
 * Friendly access-denied surface. Shown whenever the operator
 * navigates to a route they don't have the required permission for —
 * AuthGuard renders it instead of bouncing to login, and
 * `PermissionGate` renders it instead of returning null. Keeps the
 * shell + side nav intact so the operator can see where they are and
 * recover via the home button.
 */
export function AccessDeniedScreen({
  title,
  description,
  homePath = PUBLIC_PATH.LOGIN,
}: AccessDeniedScreenProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <Card className="max-w-md w-full border-primary/30 bg-primary/5">
        <CardContent className="flex flex-col items-center gap-4 py-10 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ShieldAlert className="h-7 w-7" />
          </div>
          <div className="space-y-1">
            <h2 className="text-xl font-semibold">
              {title ?? t(i18n.COMMON.ACCESS_RESTRICTED_TITLE)}
            </h2>
            <p className="text-sm text-muted-foreground">
              {description ?? t(i18n.COMMON.ACCESS_RESTRICTED_DESCRIPTION)}
            </p>
          </div>
          <Button onClick={() => navigate(homePath)}>
            <Home className="mr-2 h-4 w-4" />
            {t(i18n.COMMON.GO_HOME)}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default AccessDeniedScreen;
